import { Fragment, useEffect, useState } from 'react';
import {
    Route,
    Switch,
    useHistory,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import { useFetchSpotlights, useUpdateSpotlight } from 'utils/hooks/spotlights';
import { FiChevronsLeft } from 'react-icons/fi';
import { IconButton } from 'components/Button';
import Link from 'components/Link';
import Spotlight from 'components/SpotlightTile';
import SwalContent from 'components/SwalContent';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from 'dayjs';
import dayjsUTCPlugin from 'dayjs/plugin/utc';

dayjs.extend(dayjsUTCPlugin);
dayjs.extend(customParseFormat);

function ManageSpotlights() {
    const { data: spotlights, status } = useFetchSpotlights();
    const { path, url } = useRouteMatch();
    const history = useHistory();

    if (status === 'idle') return null;
    if (status === 'loading') return <div>Loading...</div>;
    if (status === 'error') return <div>Failed to load spotlights</div>;

    return (
        <Switch>
            <Route exact path={path}>
                <IconButton onClick={(e) => history.push(`${path}/create`)}>
                    New Spotlight
                </IconButton>
                <br />
                <br />
                <Spotlight.TileGrid>
                    {spotlights.map(
                        ({ id, title, description, images, updatedStr }) => (
                            <Spotlight.Tile
                                key={id}
                                image={images}
                                heading={description}
                                subheading={updatedStr}
                                classification={title}
                                as={Link}
                                to={`${path}/${id}`}
                            />
                        ),
                    )}
                </Spotlight.TileGrid>
            </Route>
            <Route exact path={`${path}/create`}>
                <EditSpotlight spotlights={spotlights} />
            </Route>
            <Route path={`${path}/:spotlightId`}>
                <EditSpotlight spotlights={spotlights} />
            </Route>
        </Switch>
    );
}

const newSpotlight = {
    status: 'published',
    title: '',
    description: '',
    link: '',
    images: '',
};

const fields = [
    { key: 'title', type: 'text' },
    { key: 'description', type: 'textarea' },
    { key: 'link', type: 'text' },
    { key: 'images', type: 'text' },
    { key: 'updated', type: 'date' },
];

const dateStrFormat = 'YYYY-MM-DD';
export const millisecondsToDateStr = (milliseconds) =>
    dayjs.utc(milliseconds).format(dateStrFormat);
export const dateStrToUnixTimestamp = (dateStr) =>
    dayjs.utc(dateStr, dateStrFormat).unix();

function EditSpotlight({ spotlights }) {
    const history = useHistory();
    const { spotlightId } = useParams();
    const foundSpotlight = spotlights.find(({ id }) => id === spotlightId);
    const [editing, setEditing] = useState(
        foundSpotlight
            ? {
                  ...foundSpotlight,
                  link: foundSpotlight.link || foundSpotlight.externalLink,
                  updated: millisecondsToDateStr(foundSpotlight.updated),
              }
            : { ...newSpotlight },
    );
    const { mutateAsync: updateSpotlight, isLoading } = useUpdateSpotlight();

    useEffect(() => {
        if (!spotlightId) {
            setEditing({ ...newSpotlight });
        } else if (foundSpotlight) {
            setEditing({
                ...foundSpotlight,
                link: foundSpotlight.link || foundSpotlight.externalLink,
                updated: millisecondsToDateStr(foundSpotlight.updated),
            });
        } else {
            setEditing(null);
        }
    }, [spotlightId, foundSpotlight]);

    if (!editing)
        return (
            <>
                <IconButton
                    IconLeft={FiChevronsLeft}
                    onClick={(e) => history.push(`/admin/spotlights`)}
                >
                    Back to Spotlights
                </IconButton>
                <p>Could not find spotlight with id "{spotlightId}"</p>
            </>
        );

    const handleChange = (key, value) =>
        setEditing({ ...editing, [key]: value });
    const handleCancel = () => history.push(`/admin/spotlights`);
    const handleDelete = () =>
        updateSpotlight({ ...foundSpotlight, status: 'deleted' });
    const handleSubmit = (e) => {
        e.preventDefault();
        updateSpotlight({
            status: 'published',
            title: editing.title,
            description: editing.description,
            link: editing.link,
            ...(editing.id && {
                id: editing.id,
                created: dateStrToUnixTimestamp(editing.updated),
            }),
            ...(editing.images && { images: [editing.images] }),
        });
    };

    return (
        <>
            <IconButton
                IconLeft={FiChevronsLeft}
                onClick={(e) => history.push(`/admin/spotlights`)}
            >
                Back to Spotlights
            </IconButton>
            <SwalContent.Wrapper as="form" onSubmit={handleSubmit}>
                {fields.map(({ key, type }) => {
                    if (key === 'updated' && !editing) return null;

                    const InputElement =
                        type === 'textarea' ? 'textarea' : 'input';
                    return (
                        <Fragment key={key}>
                            <label htmlFor={key}>{key}</label>
                            <InputElement
                                id={key}
                                type={type}
                                value={editing[key]}
                                onChange={(e) =>
                                    handleChange(key, e.target.value)
                                }
                                disabled={isLoading}
                                required={true}
                            />
                        </Fragment>
                    );
                })}
                <SwalContent.ButtonBar>
                    {editing.id ? (
                        <IconButton
                            disabled={isLoading}
                            intent="secondary"
                            css={`
                                border-color: ${({ theme }) => theme.red_1};
                                color: ${({ theme }) => theme.red_1};
                                margin-right: auto;
                            `}
                            onClick={handleDelete}
                        >
                            delete
                        </IconButton>
                    ) : null}
                    <IconButton
                        disabled={isLoading}
                        intent="secondary"
                        onClick={handleCancel}
                    >
                        cancel
                    </IconButton>
                    <IconButton
                        css={`
                            margin-left: ${({ theme }) => theme.space_4}px;
                        `}
                        fetching={isLoading}
                        disabled={isLoading}
                        intent="primary"
                        type="submit"
                    >
                        save
                    </IconButton>
                </SwalContent.ButtonBar>
                {isLoading ? (
                    <div role="alert" css="text-align: right;">
                        Saving your updates...
                    </div>
                ) : null}
            </SwalContent.Wrapper>
        </>
    );
}

export default ManageSpotlights;
